import { useContext } from "react";

import { Button, Grid } from "@mui/material";
import { FilterField } from "@rubi/ui";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import {
  useAlertCrimeTypesQuery,
  useBulletinTypesQuery,
  useCrimeLabel,
} from "@/api/loadAlerts";
import { AlertViewContext } from "@/components/alerts/context";
import { setLocalStorageWithExpiry } from "@/components/alerts/utils";
import { AuthStateContext } from "@/contexts/AuthContext";

import {
  FilterChip,
  FilterChipGroup,
  RenderFilterChips,
  SearchBarFilters,
} from "./SearchFilters";

export const alertFilterConfig = (
  searchParams: URLSearchParams,
  view: string,
) => [
  {
    title: "My Bulletins",
    urlParam: "myAlerts",
    value: searchParams.get("myAlerts"),
    key: "myAlerts",
    displayFullLabel: false,
    customChipDisplay: (value) => (value === "true" ? "My Bulletins" : ""),
  },
  {
    title: "My Agency Bulletins",
    urlParam: "my_agency_bulletins",
    key: "myAgencyBulletins",
    value: searchParams.get("my_agency_bulletins"),
    displayFullLabel: false,
    customChipDisplay: (value) => (value ? "My Agency Bulletins" : ""),
    customChipRemoveClick: (setSearchParams) => {
      setLocalStorageWithExpiry(
        "myAgencyBulletinsChoice",
        "deselected",
        1000 * 60 * 60 * 24,
      );
      setSearchParams((searchParams) => {
        searchParams.delete("my_agency_bulletins");
        return searchParams;
      });
    },
  },
  {
    title: "Status",
    urlParam: "resolution_status",
    value: searchParams.get("resolution_status"),
    key: "status",
    displayFullLabel: false,
    customChipDisplay: (value) =>
      value === "resolved" ? "Resolved" : value === "active" ? "Active" : "",
    customChipRemoveClick: (setSearchParams) => {
      setSearchParams((searchParams) => {
        searchParams.delete("resolution_status");
        return searchParams;
      });
    },
  },
  {
    title: "Comments",
    urlParam: "engagement",
    value: searchParams.get("engagement"),
    key: "engagement",
    displayFullLabel: false,
    customChipDisplay: (value) =>
      ({
        any: "Any engagement (1+ comments)",
        none: "No comments",
        one: "1 comment",
        few: "2–3 comments",
        many: "4+ comments",
      })[value] ?? "",
    customChipRemoveClick: (setSearchParams) => {
      setSearchParams((searchParams) => {
        searchParams.delete("engagement");
        return searchParams;
      });
    },
  },
  {
    title: "Incident Start Date",
    key: "incidentStartDate",
    urlParam: "incidentStartDate",
    value: searchParams.get("incidentStartDate"),
    displayFullLabel: true,
  },
  {
    title: "Incident End Date",
    key: "incidentEndDate",
    urlParam: "incidentEndDate",
    value: searchParams.get("incidentEndDate"),
    displayFullLabel: true,
  },
  {
    title: "Issued Start Date",
    key: "issuedStartDate",
    urlParam: "issuedStartDate",
    value: searchParams.get("issuedStartDate"),
    displayFullLabel: true,
  },
  {
    title: "Issued End Date",
    key: "issuedEndDate",
    urlParam: "issuedEndDate",
    value: searchParams.get("issuedEndDate"),
    displayFullLabel: true,
  },

  {
    title: "Police Service",
    key: "policeService",
    urlParam: "policeService",
    value: searchParams.get("policeService"),
    displayFullLabel: true,
    customChipRemoveClick: (setSearchParams) => {
      setSearchParams((searchParams) => {
        searchParams.delete("policeService");
        searchParams.delete("operationalArea");
        return searchParams;
      });
    },
  },

  {
    title: "Showing Similar",
    key: "similarTo",
    urlParam: "similarTo",
    value: searchParams.get("similarTo"),
    displayFullLabel: true,
  },
  {
    title: "Geo Boundary",
    key: "bounds",
    urlParam: "bounds",
    value: searchParams.get("bounds"),
    displayFullLabel: false,
    customChipDisplay: () => {
      // const [swlat, swlng, nelat, nelng] = value.split(",");
      // return `SW: ${Math.round(swlat)}, ${Math.round(swlng)} NE: ${Math.round(nelat)}, ${Math.round(nelng)}`;
      return "Geo Boundary";
    },

    disableRemove: view === "map",
  },
  {
    title: "Sort By Incident Time",
    urlParam: "sort_by_incident_time",
    value: searchParams.get("sort_by_incident_time"),
    key: "sortByIncidentTime",
    displayFullLabel: false,
    customChipDisplay: (value) =>
      value === "true" ? "Sort By Incident Time" : "",
  },
  {
    title: "Sort By Share Time",
    urlParam: "sort_by",
    value: searchParams.get("sort_by") === "share_time" ? "share_time" : null,
    key: "sortByShareTime",
    displayFullLabel: false,
    customChipDisplay: (value) =>
      value === "share_time" ? "Sort By Share Time" : "",
    customChipRemoveClick: (setSearchParams) => {
      const params = new URLSearchParams(searchParams);
      params.delete("sort_by_incident_time");
      params.delete("sort_by");
      params.delete("sort_order");
      setSearchParams(params);
    },
  },
  {
    title: "Operational Area",
    urlParam: "operationalArea",
    value: searchParams.get("operationalArea"),
    key: "operationalArea",
    displayFullLabel: true,
    customChipDisplay: (value: string) => value,
  },
];

export const RenderAlertFilterChips = ({
  searchParams,
  setSearchParams,
  getFilters,
}: {
  searchParams: URLSearchParams;
  setSearchParams: (searchParams: URLSearchParams) => void;
  getFilters: (
    searchParams: URLSearchParams,
    view: string,
  ) => {
    title: string;
    urlParam: string;
    value: string | null;
    displayFullLabel: boolean;
    key: string;
    customChipDisplay?: (value: string) => string;
    customChipRemoveClick?: (
      setSearchParams: (searchParams: URLSearchParams) => void,
    ) => void;
    disableRemove?: boolean;
  }[];
}) => {
  const { currentUserGroups } = useContext(AuthStateContext);
  const bulletinId = searchParams.get("bulletinId");
  const bounds = searchParams.get("bounds");
  const view = searchParams.get("view");
  const filters = getFilters(searchParams, view || "");
  const { t } = useTranslation("bulletin/filter");
  const { data: bulletinTypesForChips = [] } = useBulletinTypesQuery();
  const crimeLabel = useCrimeLabel();

  const multiSelectGroups = [
    {
      urlParam: "group_id",
      title: "Groups",
      values: currentUserGroups ? searchParams.getAll("group_id") : [],
      getLabel: (groupId: string) =>
        currentUserGroups?.find((g) => g.id === parseInt(groupId))?.name ||
        `Group ${groupId}`,
    },
    {
      urlParam: "bulletinType",
      title: t("bulletin/filter:bulletinType"),
      values: searchParams.getAll("bulletinType"),
      getLabel: (key: string) =>
        bulletinTypesForChips.find((bt) => bt.reference_name === key)?.name ??
        key,
    },
    {
      urlParam: "associatedCrime",
      title: t("bulletin/filter:associatedCrime"),
      values: searchParams.getAll("associatedCrime"),
      getLabel: crimeLabel,
    },
  ].filter((group) => group.values.length > 0);

  const removeParamValue = (urlParam: string) => (value: string) => {
    const params = new URLSearchParams(searchParams);
    params.delete(urlParam);
    searchParams
      .getAll(urlParam)
      .filter((v) => v !== value)
      .forEach((v) => params.append(urlParam, v));
    setSearchParams(params);
  };

  const clearParam = (urlParam: string) => () => {
    const params = new URLSearchParams(searchParams);
    params.delete(urlParam);
    setSearchParams(params);
  };

  const handleClearAll = () => {
    const newParams = new URLSearchParams();
    if (bulletinId) newParams.set("bulletinId", bulletinId);
    if (view === "map") newParams.set("bounds", bounds || "");
    if (view) newParams.set("view", view);
    setSearchParams(newParams);
  };

  const activeFilters = filters.filter((f) => f.value !== null);
  if (activeFilters.length === 0 && multiSelectGroups.length === 0) return null;
  else {
    const hideClearAll =
      view === "map" &&
      multiSelectGroups.length === 0 &&
      activeFilters.length === 1 &&
      activeFilters[0].urlParam === "bounds";

    return (
      <RenderFilterChips
        filterChipsActive={[
          ...activeFilters.map((filter) =>
            filter.value !== null ? (
              <FilterChip
                key={filter.key}
                title={t(`bulletin/filter:${filter.key}`)}
                value={
                  filter.key !== "policeService"
                    ? t(`bulletin/${filter.key}:${filter.value}`)
                    : `${filter.value}`
                }
                onRemove={() => {
                  if (filter.customChipRemoveClick) {
                    filter.customChipRemoveClick(setSearchParams);
                  } else {
                    searchParams.delete(filter.urlParam);
                    setSearchParams(searchParams);
                  }
                }}
                displayFullLabel={filter.displayFullLabel}
                customChipValueDisplay={(value) =>
                  filter.customChipDisplay
                    ? filter.customChipDisplay(value)
                    : value
                }
                disableRemove={filter.disableRemove}
              />
            ) : null,
          ),
          ...multiSelectGroups.map((group) => (
            <FilterChipGroup
              key={group.urlParam}
              title={group.title}
              values={group.values}
              getLabel={group.getLabel}
              onRemove={removeParamValue(group.urlParam)}
              onClear={clearParam(group.urlParam)}
            />
          )),
        ]}
        onClearAll={handleClearAll}
        hideClearAll={hideClearAll}
      />
    );
  }
};

const AlertFilters = ({
  isOpen,
  anchorEl,
  onClose,
}: {
  isOpen: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}) => {
  const { policeServices } = useContext(AlertViewContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUserGroups } = useContext(AuthStateContext);
  const { t } = useTranslation("bulletin/filter");
  const { data: crimeTypes = [] } = useAlertCrimeTypesQuery();
  const { data: bulletinTypes = [] } = useBulletinTypesQuery();
  const getBulletinLabel = (key: string) =>
    bulletinTypes.find((bt) => bt.reference_name === key)?.name ?? key;

  const statusOptions = [
    { value: "active", label: t("statusActive") },
    { value: "resolved", label: t("statusResolved") },
  ];

  const engagementOptions = [
    { value: "any", label: t("engagementAny") },
    { value: "none", label: t("engagementNone") },
    { value: "one", label: t("engagementOne") },
    { value: "few", label: t("engagementFew") },
    { value: "many", label: t("engagementMany") },
  ];

  const updateSearchParams = (key, value) => {
    const newSearchParams = new URLSearchParams(searchParams);
    value ? newSearchParams.set(key, value) : newSearchParams.delete(key);
    setSearchParams(newSearchParams);
  };

  const selectedService = policeServices.find(
    (p) => p.name === searchParams.get("policeService"),
  );
  const operationalAreas = selectedService?.rubi_org?.operational_areas ?? [];
  const operationalAreaOptions = operationalAreas.map((area) => ({
    value: area,
    label: area,
  }));

  const gridElements = [
    <Grid size={{ xs: 12, sm: "auto" }} key="my-alerts">
      <FilterField
        type="checkbox"
        label={t("myAlerts")}
        value={searchParams.get("myAlerts") === "true"}
        onChange={(checked) => updateSearchParams("myAlerts", checked)}
      />
    </Grid>,
    <Grid size={{ xs: 12, sm: "auto" }} key="creator-group-id">
      <FilterField
        type="checkbox"
        label={t("myAgencyBulletins")}
        value={searchParams.get("my_agency_bulletins") !== null}
        onChange={(checked) =>
          updateSearchParams("my_agency_bulletins", checked)
        }
      />
    </Grid>,
    <Grid size={{ xs: 12, sm: "auto" }} key="sort-by-incident-time">
      <FilterField
        type="toggle"
        title={t("sortBy")}
        ariaLabel="sort by time"
        options={[
          { value: "issue", label: t("sortByIssueTime") },
          { value: "incident", label: t("sortByIncidentTime") },
          { value: "share", label: t("sortByShareTime") },
        ]}
        value={
          searchParams.get("sort_by") === "share_time"
            ? "share"
            : searchParams.get("sort_by_incident_time") === "true"
              ? "incident"
              : "issue"
        }
        onChange={(value) => {
          const newSearchParams = new URLSearchParams(searchParams);
          // The three sort modes are mutually exclusive: reset both params,
          // then set whichever one the selected mode needs.
          newSearchParams.delete("sort_by_incident_time");
          newSearchParams.delete("sort_by");
          if (value === "incident") {
            newSearchParams.set("sort_by_incident_time", "true");
          } else if (value === "share") {
            newSearchParams.set("sort_by", "share_time");
          }
          setSearchParams(newSearchParams);
        }}
      />
    </Grid>,
    <Grid size={{ xs: 12 }} key="incident-date-from">
      <FilterField
        type="date-range"
        labelFrom={t("incidentStartDate")}
        labelTo={t("incidentEndDate")}
        startDate={searchParams.get("incidentStartDate") || ""}
        endDate={searchParams.get("incidentEndDate") || ""}
        onStartChange={(value) =>
          updateSearchParams("incidentStartDate", value)
        }
        onEndChange={(value) => updateSearchParams("incidentEndDate", value)}
      />
    </Grid>,
    <Grid size={{ xs: 12 }} key="issued-date-from">
      <FilterField
        type="date-range"
        labelFrom={t("issuedStartDate")}
        labelTo={t("issuedEndDate")}
        startDate={searchParams.get("issuedStartDate") || ""}
        endDate={searchParams.get("issuedEndDate") || ""}
        onStartChange={(value) => updateSearchParams("issuedStartDate", value)}
        onEndChange={(value) => updateSearchParams("issuedEndDate", value)}
      />
    </Grid>,
    <Grid size={{ xs: 12, sm: 6 }} key="bulletin-type">
      <FilterField
        type="multiselect"
        id="select-bulletin-type"
        label={t("bulletinType")}
        enableSelectAll
        selectAllLabel={t("selectAll")}
        deselectAllLabel={t("deselectAll")}
        options={bulletinTypes.map((bt) => ({
          value: bt.reference_name,
          label: bt.name,
        }))}
        value={searchParams.getAll("bulletinType").map((type) => ({
          value: type,
          label: getBulletinLabel(type),
        }))}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value?.value}
        onChange={(newValue) => {
          const newParams = new URLSearchParams(searchParams);
          newParams.delete("bulletinType");
          const normalizedBulletinTypes = Array.from(
            new Set(newValue.map((v) => v.value)),
          ).sort();
          normalizedBulletinTypes.forEach((type) =>
            newParams.append("bulletinType", type),
          );
          setSearchParams(newParams);
        }}
      />
    </Grid>,
    <Grid size={{ xs: 12, sm: 6 }} key="associated-crime">
      <FilterField
        type="multiselect"
        id="select-crime"
        label={t("associatedCrime")}
        enableSelectAll
        selectAllLabel={t("selectAll")}
        deselectAllLabel={t("deselectAll")}
        options={crimeTypes}
        value={searchParams
          .getAll("associatedCrime")
          .map((c) => crimeTypes.find((ct) => ct.crime === c))
          .filter((c): c is NonNullable<typeof c> => Boolean(c))}
        getOptionLabel={(option) => option.name ?? option.crime}
        isOptionEqualToValue={(option, value) => option.crime === value?.crime}
        groupBy={(option) => t(`bulletin/associatedCrime:${option.group}`)}
        onChange={(newValue) => {
          const newParams = new URLSearchParams(searchParams);
          newParams.delete("associatedCrime");
          newValue.forEach((c) => {
            if (c?.crime) newParams.append("associatedCrime", c.crime);
          });
          setSearchParams(newParams);
        }}
      />
    </Grid>,
    <Grid size={{ xs: 12, sm: 6 }} key="police-service">
      <FilterField
        type="select"
        id="police-service-select-autocomplete"
        label={t("issuedPoliceService")}
        options={policeServices}
        value={
          policeServices.find(
            (p) => p.name === searchParams.get("policeService"),
          ) || null
        }
        getOptionLabel={(option) => option.name}
        onChange={(newValue) => {
          const newParams = new URLSearchParams(searchParams);
          const nextService = newValue?.name ?? null;
          const serviceChanged =
            nextService !== searchParams.get("policeService");
          if (nextService) {
            newParams.set("policeService", nextService);
          } else {
            newParams.delete("policeService");
          }
          if (serviceChanged) {
            newParams.delete("operationalArea");
          }
          setSearchParams(newParams);
        }}
      />
    </Grid>,
    ...(operationalAreas.length > 0
      ? [
          <Grid size={{ xs: 12, sm: 6 }} key="operational-area">
            <FilterField
              type="select"
              id="operational-area-select"
              label={t("operationalArea")}
              options={operationalAreaOptions}
              value={
                operationalAreaOptions.find(
                  (o) => o.value === searchParams.get("operationalArea"),
                ) || null
              }
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) =>
                option.value === value?.value
              }
              onChange={(newValue) =>
                updateSearchParams("operationalArea", newValue?.value)
              }
            />
          </Grid>,
        ]
      : []),
    <Grid size={{ xs: 12, sm: 6 }} key="groups-filter">
      <FilterField
        type="multiselect"
        id="groups-select-autocomplete"
        label={t("groups")}
        enableSelectAll
        selectAllLabel={t("selectAll")}
        deselectAllLabel={t("deselectAll")}
        options={
          currentUserGroups?.filter((group) => !group.is_self_group) || []
        }
        value={searchParams
          .getAll("group_id")
          .map((id) => currentUserGroups?.find((g) => g.id === parseInt(id)))
          .filter((group): group is NonNullable<typeof group> =>
            Boolean(group),
          )}
        getOptionLabel={(option) => option?.name || ""}
        onChange={(newValue) => {
          const newParams = new URLSearchParams(searchParams);
          newParams.delete("group_id");
          newValue.forEach((group) => {
            if (group?.id) {
              newParams.append("group_id", group.id.toString());
            }
          });
          setSearchParams(newParams);
        }}
      />
    </Grid>,
    <Grid size={{ xs: 12, sm: 6 }} key="resolution-status">
      <FilterField
        type="select"
        id="resolution-status-select"
        label={t("status")}
        options={statusOptions}
        value={
          statusOptions.find(
            (o) => o.value === searchParams.get("resolution_status"),
          ) || null
        }
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value?.value}
        onChange={(newValue) =>
          updateSearchParams("resolution_status", newValue?.value)
        }
      />
    </Grid>,
    <Grid size={{ xs: 12, sm: 6 }} key="engagement">
      <FilterField
        type="select"
        id="engagement-select"
        label={t("engagement")}
        options={engagementOptions}
        value={
          engagementOptions.find(
            (o) => o.value === searchParams.get("engagement"),
          ) || null
        }
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value?.value}
        onChange={(newValue) =>
          updateSearchParams("engagement", newValue?.value)
        }
      />
    </Grid>,
    <Grid size={{ xs: 12 }} key="clear-filters">
      <Button
        variant="outlined"
        color="secondary"
        fullWidth
        onClick={() => setSearchParams(new URLSearchParams())}
        disabled={searchParams.toString() === ""}
        sx={{}}
      >
        {t("clear")}
      </Button>
    </Grid>,
  ];

  return (
    <SearchBarFilters
      isOpen={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      gridElements={gridElements}
    />
  );
};

export default AlertFilters;
